import * as Sentry from '@sentry/astro';
import { gtmBasicEvent } from '@klaviyo-mkt/gatsby-components/shared/utils/google-tag-manager';
import type { IGTMEvent } from '@klaviyo-mkt/gatsby-components/shared/utils/google-tag-manager';

// To do: configure Astro version of sentry
import { VALID_ACADEMY_RESOURCE_TYPES } from '@klaviyo-mkt/gatsby-components/organisms/unified-search/constants';

export const getResourceTypeLabel = (resourceType: string) =>
  `${resourceType === 'Getting started' ? 'Getting started guide' : resourceType}`;

export const pluralizeResourceType = (resourceType: string) => {
  if (resourceType.endsWith('s')) {
    return resourceType;
  }

  return `${getResourceTypeLabel(resourceType)}s`;
};

export const courseRegisterEventData = (
  { resourceCategory, resourceName, non_interaction_flag = true }: {
    resourceCategory: string;
    resourceName: string;
    non_interaction_flag?: boolean
  },
) => ({
  'event': 'klaviyoEvent',
  'category': resourceCategory, // [type] eg. certificate, course, etc
  'action': 'academy_register',
  'label': resourceName, // [name] eg. 'Make API calls to retrieve and manipulate data in your Klaviyo account'
  non_interaction_flag,
});

export const extractResourceTypeTags = (tags: (string[] | readonly string[]) = []) => {
  return tags
    .filter((tag) => tag.startsWith('type:'))
    .map((tag) => tag.split(':')[1]);
};

export const getResourceType = (tags: (string[] | readonly string[]) = []) => {
  const resourceTypeTags = extractResourceTypeTags(tags);
  return resourceTypeTags
    .find((tag) => VALID_ACADEMY_RESOURCE_TYPES.includes(tag)) || 'Course';
};

export const registerForResource = async ({
  resourceId,
  resourceType,
  registrationUrl,
  withKlaviyoTrack = true,
}: {
  resourceId: string;
  resourceType: string;
  registrationUrl: string;
  withKlaviyoTrack?: boolean;
}) => {
  try {
    const response = await fetch(registrationUrl, { credentials: 'include', redirect: 'follow' });
    if (response.status !== 200) {
      throw new Error(`Failed to register for ${resourceType} ${resourceId}. Received status code: ` + response.status);
    }

    if (withKlaviyoTrack) {
      await window.klaviyo?.track('Registered for Academy resource', {
        resourceId,
        resourceType,
        url: `${window.location.origin}${window.location.pathname}`,
      });
    }

    return true;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
};

export const handleRegistrationButtonClick = ({
  event,
  isUserLoggedIn = false,
  registrationUrl,
  resourceId,
  resourceType,
  resourceName,
  sendRegistrationEvent,
}: {
  event: Event;
  isUserLoggedIn: boolean;
  registrationUrl?: string | null;
  resourceId: string;
  resourceType: string;
  resourceName: string;
  sendRegistrationEvent: boolean;
}) => {
  if (!isUserLoggedIn || !registrationUrl) {
    return false;
  }

  event.preventDefault();

  if (sendRegistrationEvent) {
    const eventData: IGTMEvent = courseRegisterEventData({
      resourceName,
      resourceCategory: resourceType,
      non_interaction_flag: false,
    });
    gtmBasicEvent(eventData);
  }

  registerForResource({
    resourceId,
    resourceType,
    registrationUrl,
  }).then((success) => {
    if (success) {
      window.location.assign((event.target as HTMLAnchorElement).href);
    }
  });
};
